import {
  ActionIcon,
  Anchor,
  Box,
  Button,
  Center,
  FileButton,
  Group,
  Menu,
  Modal,
  Paper,
  Portal,
  ScrollArea,
  Stack,
  Text,
  TextInput,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import {
  IconCamera,
  IconFolder,
  IconPaperclip,
  IconSend,
  IconUpload,
} from "@tabler/icons-react";
import * as df from "date-fns";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { useParams } from "wouter";
import { client } from "@/api-client";
import type { FileOutput, GroupChatMessage } from "@/api-types";
import { FancyLoader } from "@/components/FancyLoader";
import { useFileUploadModal } from "@/components/FileUploadModal";
import { useFolderNavigator } from "@/components/FolderNavigator";
import { GroupLogo } from "@/components/GroupLogo";
import { Header } from "@/components/Header";
import { UniversalRoutes, useNavigator } from "@/routers/routes";
import { useUser } from "@/UserContext";
import { useWebSocket } from "@/websocket/client";

const MAX_MESSAGES = 50;

const GroupChatContent = ({ groupId }: { groupId: string }) => {
  const [messages, setMessages] = useState<GroupChatMessage[]>([]);
  const [endingAt, setEndingAt] = useState<string | undefined>(undefined);
  const [message, setMessage] = useState("");
  const [hasMore, setHasMore] = useState(false);
  const chatRef = useRef<HTMLDivElement>(null);
  const scrollAreaRef = useRef<HTMLDivElement>(null);
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  const [isFirstLoad, setIsFirstLoad] = useState(true);
  const user = useUser();
  const { navigator } = useNavigator();
  const [cameraOpened, { open: openCamera, close: closeCamera }] =
    useDisclosure(false);

  const { data: historicMessages } = client.useQuery(
    "get",
    "/universal/groups/{groupId}/chat/messages",
    {
      params: {
        path: {
          groupId,
        },
        query: {
          limit: MAX_MESSAGES,
          endingAt,
        },
      },
    },
  );

  // Get group participants to map sender IDs to names
  const { data: groupParticipants } = client.useQuery(
    "get",
    user.role === "trainer"
      ? "/trainer/groups/{groupId}"
      : "/owner/groups/{groupId}",
    {
      params: {
        path: {
          groupId,
        },
      },
    },
  );

  const { lastMessage } = useWebSocket();

  const sendMessage = client.useMutation(
    "post",
    "/universal/groups/{groupId}/chat/messages",
  );

  const fileUploadMutation = client.useMutation(
    "post",
    "/universal/file-upload",
  );
  const addToLibraryMutation = client.useMutation("post", "/universal/files/");

  const handleSendFileMessage = async (file: FileOutput) => {
    try {
      const responseMessage = await sendMessage.mutateAsync({
        params: {
          path: {
            groupId,
          },
        },
        body: {
          message: file.name,
          objectId: file.id,
          specialType: "FILE",
        },
      });

      addMessages([responseMessage]);
    } catch (error) {
      console.error("Error sending file message:", error);
    }
  };

  // Use hooks for file navigation and upload
  const { open: openFolderNavigator, modal: folderNavigatorModal } =
    useFolderNavigator({
      onFileSelect: handleSendFileMessage,
    });

  const { open: openFileUpload, modal: fileUploadModal } = useFileUploadModal({
    onSuccess: handleSendFileMessage,
  });

  const addMessages = useCallback((newMessages: GroupChatMessage[]) => {
    if (!newMessages.length) {
      return;
    }

    setMessages((prev) => {
      const allMessages = [...newMessages, ...prev];
      allMessages.sort(
        (a, b) => new Date(a.sentAt).getTime() - new Date(b.sentAt).getTime(),
      );
      const msgs = allMessages.filter(
        (message, index) =>
          allMessages[index - 1]?.id !== message.id &&
          message.id !== "local-new",
      );
      return msgs;
    });
  }, []);

  useEffect(() => {
    if (historicMessages) {
      setHasMore(historicMessages.messages.length >= MAX_MESSAGES);
    }
    const newMessages = [...(historicMessages?.messages ?? [])];

    if (lastMessage && lastMessage.type === "group_chat_message") {
      const groupMessage = lastMessage.content as GroupChatMessage;
      if (groupMessage.groupId === groupId) {
        newMessages.push(groupMessage);
      }
    }

    addMessages(newMessages);

    if (isFirstLoad) {
      setIsFirstLoad(false);
      setShouldScrollToBottom(true);
    } else {
      const element = scrollAreaRef.current?.querySelector(
        ".mantine-ScrollArea-viewport",
      );
      if (!element) {
        return;
      }
      if (
        element.scrollTop + element.clientHeight >=
        element.scrollHeight - 20
      ) {
        setShouldScrollToBottom(true);
      }
    }
  }, [historicMessages, lastMessage, addMessages, isFirstLoad, groupId]);

  useLayoutEffect(() => {
    if (!scrollAreaRef.current || !shouldScrollToBottom) {
      return;
    }

    const viewport = scrollAreaRef.current.querySelector(
      ".mantine-ScrollArea-viewport",
    );
    if (viewport) {
      viewport.scrollTop = viewport.scrollHeight;
      setShouldScrollToBottom(false);
    }
  }, [shouldScrollToBottom]);

  const handleSendMessage = async () => {
    if (!message.trim()) return;

    setMessage("");
    setMessages((prev) => [
      ...prev,
      {
        id: "local-new",
        groupId,
        sentAt: new Date().toUTCString(),
        content: message,
        type: "USER_MESSAGE" as const,
        senderId: user.accountId,
        senderRole: user.role,
      },
    ]);
    setShouldScrollToBottom(true);

    try {
      const responseMessage = await sendMessage.mutateAsync({
        params: {
          path: {
            groupId,
          },
        },
        body: {
          message: message,
        },
      });

      addMessages([responseMessage]);
    } catch (error) {
      console.error("Error sending message:", error);
    }
  };

  const handleTakePicture = async (file: File | null) => {
    if (!file) return;

    try {
      closeCamera();

      const fileUpload = await fileUploadMutation.mutateAsync({});
      const formData = new FormData();

      Object.entries(fileUpload.params.fields).forEach(([k, v]) =>
        formData.append(k, v),
      );
      formData.append("file", file);

      // Upload file
      await fetch(fileUpload.params.url, {
        method: "POST",
        body: formData,
      });

      // Add to library
      const result = await addToLibraryMutation.mutateAsync({
        body: {
          uploadId: fileUpload.id,
          originalName: file.name,
          name: `Photo_${new Date().toISOString().slice(0, 19).replace(/:/g, "-")}`,
          description: "Photo taken from group chat",
          type: "IMAGE",
          parentFolderId: null,
          isPublic: false,
        },
      });

      // Send the file message
      if (result) {
        await handleSendFileMessage(result);
      }
    } catch (error) {
      console.error("Error uploading picture:", error);
    }
  };

  const loadMore = () => {
    if (messages.length > 0) {
      setEndingAt(messages[0]?.sentAt);
    }
  };

  const formatDate = (date: Date) => {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return "Today";
    } else if (date.toDateString() === yesterday.toDateString()) {
      return "Yesterday";
    }
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const isTrainer = user.role === "trainer";

  // Function to get sender's name from participant data
  const getSenderName = (senderId: string, senderRole: string) => {
    if (senderId === user.accountId) {
      return null; // Don't show name for current user
    }

    if (senderRole === "trainer") {
      // For owners, get trainer info from groupParticipants.trainer
      if (
        user.role === "owner" &&
        groupParticipants &&
        "trainer" in groupParticipants
      ) {
        return groupParticipants.trainer.firstName;
      }
      // For trainers, they are the trainer themselves, so show "Trainer"
      return "Trainer";
    }

    if (senderRole === "owner") {
      // For trainers, get owner info from groupParticipants.participants
      if (
        user.role === "trainer" &&
        groupParticipants &&
        "participants" in groupParticipants
      ) {
        const participant = groupParticipants.participants.find(
          (p) => p.owner?.id === senderId,
        );
        return participant?.owner?.firstName || "Member";
      }
      // For owners, they are the member themselves, so show "Member"
      return "Member";
    }

    return senderRole === "trainer" ? "Trainer" : "Member";
  };

  const renderMessageBody = (
    message: GroupChatMessage,
    isCurrentUser: boolean,
  ) => {
    const senderName = isCurrentUser
      ? "You"
      : getSenderName(message.senderId, message.senderRole);
    if (message.specialType === "FILE") {
      return (
        <Text fs="italic">
          {`${senderName} sent a file: `}
          <Anchor
            td="underline"
            c={isCurrentUser ? "white" : "black"}
            onClick={navigator({
              path: UniversalRoutes.FILE_PREVIEW,
              // biome-ignore lint/style/noNonNullAssertion: whatever
              params: { fileId: message.objectId! },
            })}
          >
            {message.content}
          </Anchor>
        </Text>
      );
    }
    return <Text>{message.content}</Text>;
  };

  return (
    <Stack>
      <ScrollArea h={"70vh"} ref={scrollAreaRef}>
        <Stack ref={chatRef}>
          {hasMore && (
            <Center>
              <Text c="blue.8" style={{ cursor: "pointer" }} onClick={loadMore}>
                Load older
              </Text>
            </Center>
          )}
          {messages.flatMap((message, index) => {
            const isCurrentUser = message.senderId === user.accountId;
            const currentDate = new Date(message.sentAt);
            const previousMessage = messages[index - 1];
            const previousDate = previousMessage
              ? new Date(previousMessage.sentAt)
              : null;
            const showDateDivider =
              !previousDate || !df.isSameDay(currentDate, previousDate);

            return [
              showDateDivider && (
                <Center key={`divider-${message.id}`} mb="xs">
                  <Text size="sm" c="gray.6">
                    {formatDate(currentDate)}
                  </Text>
                </Center>
              ),
              <Box
                key={message.id}
                style={{
                  alignSelf: isCurrentUser ? "flex-end" : "flex-start",
                }}
              >
                <Paper
                  p="sm"
                  radius="lg"
                  bg={isCurrentUser ? "blue" : "gray.1"}
                  c={isCurrentUser ? "white" : "black"}
                  style={{
                    wordBreak: "break-word",
                  }}
                >
                  {!isCurrentUser && (
                    <Text size="xs" fw={500} mb="xs">
                      {getSenderName(message.senderId, message.senderRole)}
                    </Text>
                  )}
                  {renderMessageBody(message, isCurrentUser)}
                  <Text size="xs" ta="right">
                    {new Date(message.sentAt).toLocaleTimeString(undefined, {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </Text>
                </Paper>
              </Box>,
            ];
          })}
        </Stack>
      </ScrollArea>

      {isTrainer ? (
        <Group>
          <TextInput
            placeholder="Type a message..."
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            style={{ flex: 1 }}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                handleSendMessage();
              }
            }}
          />
          <ActionIcon
            color="blue"
            variant="filled"
            size="lg"
            radius="xl"
            onClick={handleSendMessage}
            disabled={!message.trim()}
          >
            <IconSend size="1.2em" />
          </ActionIcon>
          <Menu shadow="md" width={200} position="top">
            <Menu.Target>
              <ActionIcon color="blue" variant="filled" size="lg" radius="xl">
                <IconPaperclip size="1.2em" />
              </ActionIcon>
            </Menu.Target>
            <Menu.Dropdown>
              <Menu.Item
                leftSection={<IconFolder size={14} />}
                onClick={openFolderNavigator}
              >
                Pick from Library
              </Menu.Item>
              <Menu.Item
                leftSection={<IconUpload size={14} />}
                onClick={openFileUpload}
              >
                Upload File
              </Menu.Item>
              <Menu.Item
                leftSection={<IconCamera size={14} />}
                onClick={openCamera}
              >
                Take Picture
              </Menu.Item>
            </Menu.Dropdown>
          </Menu>
        </Group>
      ) : (
        <Paper p="md" withBorder>
          <Text c="dimmed" ta="center">
            Only trainers can send messages in group chats
          </Text>
        </Paper>
      )}

      <Portal>
        <Modal
          opened={cameraOpened}
          onClose={closeCamera}
          title="Take a Picture"
          centered
          size="sm"
        >
          <Stack align="center" gap="md">
            <Text size="sm" c="dimmed" ta="center">
              Take a picture to send in the group chat
            </Text>
            <FileButton onChange={handleTakePicture} accept="image/*" capture>
              {(props) => (
                <Button {...props} variant="filled" size="lg">
                  Take Photo
                </Button>
              )}
            </FileButton>
            <FileButton onChange={handleTakePicture} accept="image/*">
              {(props) => (
                <Button {...props} variant="outline" size="lg">
                  Choose from Gallery
                </Button>
              )}
            </FileButton>
          </Stack>
        </Modal>

        {/* Render modals from hooks */}
        {folderNavigatorModal}
        {fileUploadModal}
      </Portal>
    </Stack>
  );
};

export const GroupChat = () => {
  const { groupId } = useParams<{ groupId: string }>();
  const { navigator } = useNavigator();
  const { data: groupInfo, isLoading } = client.useQuery(
    "get",
    "/universal/groups/chat",
  );

  if (isLoading) {
    return <FancyLoader />;
  }

  const group = groupInfo?.groups.find((g) => g.groupId === groupId);

  if (!group) {
    return (
      <Stack>
        <Header title="Group Chat" />
        <Center>
          <Text>
            Group doesn't exist or it doesn't have a chat enabled. Or you don't
            have access to this group.
          </Text>
        </Center>
      </Stack>
    );
  }

  return (
    <Stack>
      <Header
        title={group.name}
        actionControl={
          <Box
            style={{ cursor: "pointer" }}
            onClick={() => {
              navigator({
                path: UniversalRoutes.GROUPS.DETAIL,
                params: { groupId },
              })();
            }}
          >
            <GroupLogo name={group.name} size={32} />
          </Box>
        }
      />
      <GroupChatContent groupId={groupId} />
    </Stack>
  );
};
